/**
 * Onde o canhoto foi anexado, do lado da tela.
 *
 * A coordenada é a do celular no instante em que o motorista confirma o envio: com foto da câmera é o
 * lugar da foto, com foto da galeria é onde ele estava ao anexar. Quem mede o raio é o backend; aqui
 * só se decide o que dá para mostrar e como escrever.
 */

/**
 * Cópia de `coordenadaUtilizavel` do `app-core-api` (`src/utils/conferencia-canhoto.util.ts`). Não há
 * pacote compartilhado entre os repositórios, então é cópia mesmo — mudou lá, tem que mudar aqui.
 *
 * O endereço de ponto antigo pode estar gravado como texto, e o zero é o que o cadastro antigo põe no
 * lugar do campo vazio.
 */
function coordenadaUtilizavel(latitude, longitude) {
  const lat = paraNumero(latitude);
  const lon = paraNumero(longitude);
  if (lat === null || lon === null) return null;
  if (lat === 0 || lon === 0) return null;
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
  return { latitude: lat, longitude: lon };
}

function paraNumero(valor) {
  if (typeof valor === "number") return Number.isFinite(valor) ? valor : null;
  if (typeof valor !== "string") return null;
  const texto = valor.trim().replace(",", ".");
  if (!texto) return null;
  const n = Number(texto);
  return Number.isFinite(n) ? n : null;
}

/**
 * `{0, 0}` chega aqui de verdade: o backend grava a localização sempre que vem número, e só a
 * conferência recusa o zero. O ponto fica SEM_LOCALIZACAO com uma coordenada no meio do Atlântico.
 *
 * Precisão zero é o Android dizendo que não mediu, não um GPS perfeito.
 */
export function localDoCanhoto(ponto) {
  const local = ponto?.receiptLocation;
  const coordenada = coordenadaUtilizavel(local?.latitude, local?.longitude);
  if (!coordenada) return null;
  const precisao =
    Number.isFinite(local.accuracy) && local.accuracy > 0 ? local.accuracy : null;
  return { ...coordenada, precisao };
}

/**
 * O endereço até onde o mapa traça a linha. Não existe quando a conferência disse que a referência não
 * presta — e aprovar a exceção troca o status sem consertar o endereço, então vale o que a máquina
 * classificou antes da aprovação.
 */
export function referenciaDoCanhoto(ponto) {
  const classificado =
    ponto?.receiptStatus === "EXCECAO_APROVADA"
      ? ponto?.receiptException?.previousStatus
      : ponto?.receiptStatus;
  if (classificado === "SEM_REFERENCIA") return null;
  return coordenadaUtilizavel(ponto?.latitude, ponto?.longitude);
}

/** Distância nula não é zero: sem medida, a tela não diz distância nenhuma. */
export function textoDaDistancia(metros) {
  return Number.isFinite(metros) ? `${Math.round(metros)} m do endereço` : null;
}

export function textoDaPrecisao(metros) {
  return Number.isFinite(metros) && metros > 0
    ? `precisão de ${Math.max(1, Math.round(metros))} m`
    : "precisão não informada";
}

/**
 * Ponto como separador decimal: "-19,46231, -44,24718" não se lê, e assim a coordenada cola direto na
 * busca de qualquer mapa. Cinco casas é um metro, mais fino que o GPS de qualquer celular.
 */
export function textoDoLocal(local) {
  if (!local) return "";
  return `Anexado em ${local.latitude.toFixed(5)}, ${local.longitude.toFixed(5)} · ${textoDaPrecisao(local.precisao)}`;
}
